import type { Block, BlockStyle, BlockType } from './types'

// Block factory — defaults ported from the prototype's edMake (L1371) with
// the email-safe adjustments noted inline.

let counter = 0
export function newBlockId(): string {
  counter = (counter + 1) % 1e6
  const rand = Math.random().toString(36).slice(2, 8)
  return `b${Date.now().toString(36)}${counter.toString(36)}${rand}`
}

// Default footer copy. {{store_address}} is not a backend token, so the
// prototype's address line is left out.
export const FOOTER_HTML =
  '<strong>{{store_name}}</strong><br><br>You’re receiving this because you joined {{store_name}} Rewards or opted in at checkout.<br>For adults 19+ only. Please consume responsibly.'

export const TABLE_ROWS: string[][] = [
  ['Tier', 'Points', 'Perks'],
  ['Bronze', '0', '1 point per $1'],
  ['Silver', '500', '1.25 points per $1, birthday gift'],
  ['Gold', '1,500', '1.5 points per $1, early access'],
  ['Platinum', '3,000', '2 points per $1, free delivery'],
]

const BASE_STYLE: BlockStyle = { padT: 12, padB: 12, padX: 24, bg: '' }

function clone<T>(v: T): T {
  return JSON.parse(JSON.stringify(v ?? null)) as T
}

const defaultsFor = (
  type: BlockType,
  p: Record<string, any>,
): { props: Record<string, any>; style: Partial<BlockStyle> } => {
  switch (type) {
    case 'columns': {
      const ratio = String(p.ratio || '50-50')
      const n = ratio.split('-').length
      return {
        props: {
          ratio,
          cols: Array.from({ length: n }, () => []),
          gap: 16,
          stack: true,
        },
        style: {},
      }
    }
    case 'text': {
      const heading = p.preset === 'h1' || p.preset === 'h2'
      return {
        props: heading
          ? {
              preset: p.preset,
              size: p.preset === 'h1' ? 30 : 22,
              html: 'Your headline here',
              color: '',
              align: 'left',
            }
          : {
              preset: 'p',
              size: 15,
              html: 'Write something your customers will want to read. Add their name, points or tier from the Personalize tab.',
              color: '',
              align: 'left',
            },
        style: heading ? { padT: 20, padB: 6 } : {},
      }
    }
    case 'image':
      return {
        props: { src: '', alt: '', link: '', width: 100, radius: 0 },
        style: {},
      }
    case 'split':
      // The prototype's generated SVG artwork can't ship in email (data: SVG
      // is blocked by most clients), so src starts empty.
      return {
        props: {
          src: '',
          alt: '',
          link: '',
          side: 'left',
          imgW: 40,
          shape: 'rounded',
          valign: 'middle',
          gap: 24,
          stack: true,
          align: 'left',
          title: 'Introduce something new',
          titleSize: 20,
          titleColor: '',
          titleUpper: false,
          html: 'Tell the story behind a new product, feature or article in two or three sentences. It gives customers enough context to click through.',
          bodySize: 15,
          bodyColor: '',
          showBtn: true,
          btnStyle: 'button',
          btnLabel: 'Learn more',
          btnUrl: 'https://',
          btnBg: '',
          btnColor: '#FFFFFF',
          btnRadius: 6,
        },
        style: { padT: 20, padB: 20 },
      }
    case 'button':
      return {
        props: {
          label: 'Shop now',
          url: 'https://',
          bg: '',
          color: '#FFFFFF',
          radius: 6,
          size: 'md',
          full: false,
          align: 'center',
        },
        style: { padT: 16, padB: 16 },
      }
    case 'divider':
      return {
        props: { color: '#E5E7EB', thick: 1, line: 'solid', width: 100 },
        style: {},
      }
    case 'spacer':
      return { props: { h: 32 }, style: { padT: 0, padB: 0 } }
    case 'table':
      return {
        props: {
          rows: clone(TABLE_ROWS),
          header: true,
          striped: true,
          border: '#E5E7EB',
          size: 13,
        },
        style: {},
      }
    case 'social':
      return {
        props: {
          nets: {
            instagram: true,
            facebook: true,
            x: true,
            tiktok: false,
            youtube: false,
          },
          urls: { instagram: '', facebook: '', x: '', tiktok: '', youtube: '' },
          align: 'center',
          shape: 'solid',
          // '' = the email's brand colour.
          color: '',
          // Space between icons, px.
          gap: 8,
        },
        style: { padT: 16, padB: 8 },
      }
    case 'header':
      // logoMode 'auto' = logo image (props.logoImg, else the store brand
      // logo), falling back to the rich-text logo; 'text' forces the text.
      // Empty link urls fall back to the store url at compile time.
      return {
        props: {
          logoText: '',
          logoHtml: '<b>{{store_name}}</b>',
          logoFont: '',
          logoSize: 22,
          logoColor: '',
          logoSpacing: '0.14em',
          logoImg: '',
          logoMode: 'auto',
          align: 'center',
          showLinks: true,
          links: [
            { label: 'Shop', url: '' },
            { label: 'Rewards', url: '' },
            { label: 'Store locator', url: '' },
          ],
          linkColor: '',
          linkSize: 13,
        },
        style: { padT: 22, padB: 16 },
      }
    case 'footer':
      return {
        props: {
          html: FOOTER_HTML,
          align: 'center',
          size: 12,
          color: '#6B7280',
        },
        style: { padT: 24, padB: 28, bg: '#F9FAFB' },
      }
    case 'product':
      return {
        props: {
          source: 'best',
          ids: [],
          count: 3,
          perRow: 3,
          showPrice: true,
          showMeta: true,
          showBtn: true,
          btnLabel: 'Shop now',
          title: 'Featured products',
          showTitle: false,
          titlePreset: 'h2',
          titleSize: 22,
          titleColor: '',
          titleAlign: 'center',
          saleBadge: true,
        },
        style: {},
      }
    case 'coupon':
      // `code` is a real store coupon code. Picking one copies its wording
      // into title / note / scopeText and sets `prefilled`; until then (and
      // for older blocks) title / note fall back to the coupon's own title /
      // description at compile time.
      return {
        props: {
          code: '',
          title: '',
          note: '',
          line: 'dashed',
          showScope: true,
          scopeText: '',
        },
        style: { padT: 16, padB: 16 },
      }
    case 'wallet':
      return {
        props: {
          progress: true,
          // No backend value to fill: the compiler never emits this row.
          reward: false,
          barcode: true,
          guest: 'join',
          color: '',
          joinUrl: '',
          // Join-card line from the store's loyalty rules (set by the editor
          // when known); empty = generic copy.
          joinText: '',
        },
        style: { padT: 16, padB: 16 },
      }
    case 'cart':
      return {
        props: {
          title: 'Still in your cart',
          showTitle: true,
          titlePreset: 'h2',
          titleSize: 22,
          titleColor: '',
          titleAlign: 'center',
          showImage: true,
          showMeta: true,
          showPrice: true,
        },
        style: {},
      }
    case 'rewards':
      return {
        props: { count: 4, locked: true, title: 'Rewards you can unlock' },
        style: {},
      }
    case 'tierbar':
      return {
        props: {
          text: '{{first_name}}, you’re {{points_to_next_tier}} points away from {{next_tier}}',
          color: '',
        },
        style: { padT: 20, padB: 20 },
      }
    case 'countdown':
      return {
        props: { end: '', label: 'Sale ends in', color: '' },
        style: { padT: 16, padB: 16 },
      }
    case 'video':
      return {
        props: { url: '', thumb: '', caption: '', radius: 8 },
        style: {},
      }
    case 'html':
      return {
        props: {
          code: '<p style="margin:0;text-align:center;font-size:13px;color:#6B7280;">Custom HTML. Paste your own markup here.</p>',
        },
        style: {},
      }
    default:
      return { props: {}, style: {} }
  }
}

export function makeBlock(
  type: BlockType,
  props: Record<string, any> = {},
): Block {
  const given = clone(props) || {}
  const d = defaultsFor(type, given)
  return {
    id: newBlockId(),
    type,
    props: { ...d.props, ...given },
    style: { ...BASE_STYLE, ...d.style },
    // num defaults to the prototype's 1,500-point threshold for the points
    // rule; the compiler emits num 0 for every other rule.
    cond: { mode: 'all', rule: 'tier', values: [], num: 1500 },
  }
}

export function cloneBlock(b: Block): Block {
  const c = clone(b)
  const reId = (x: Block) => {
    x.id = newBlockId()
    if (x.type === 'columns' && Array.isArray(x.props?.cols)) {
      for (const col of x.props.cols as Block[][])
        for (const child of col) reId(child)
    }
  }
  reId(c)
  return c
}
